// @mhmo91/schmancy/state — reactive state primitive.
//
// Module-scoped singletons keyed by namespace. Each state exposes `value`,
// `signal` (TC39), `$` (RxJS), `ready`, and `loaded`. Three call shapes:
//   const cart = state<CartState>('hannah/cart').session({ items: [], total: 0 })
//   const cart = state('hannah/cart').session(initialCart)               // typed-const
//   declare module '@mhmo91/schmancy/state' {                            // registry
//     interface SchmancyStateRegistry { 'hannah/cart': CartState }
//   }

import { Signal, computed as _litComputed } from '@lit-labs/signals'
// `@lit/context` re-exports `ContextRequestEvent` as `ContextEvent` from
// its public entry. Same class, just a shorter public name.
import { ContextEvent as ContextRequestEvent, createContext } from '@lit/context'
import { Observable, from, type MonoTypeOperatorFunction } from 'rxjs'
import { produce, type Draft } from 'immer'

import { createAdapter, type StorageAdapter, type StorageBackend } from './persist'
import {
	_activeHost,
	resolveActiveHost,
	stateContextKey,
	_devShouldWarnContextualRead,
	_devWrapComputedCallback,
} from './active-host'
// Side-effect import: registers the `<schmancy-context>` element so users
// only need to import from `@mhmo91/schmancy/state` to get both the factory
// and the scoping primitive.
import './schmancy-context'

export type { StorageBackend } from './persist'
export { Signal } from '@lit-labs/signals'
export { _activeHost } from './active-host'
export { SchmancyContext } from './schmancy-context'
export { registerContext, captureContext, captureContexts, restoreContext, contextChanges$ } from './context'
export type { ContextValue, ContextSnapshot, ContextScope, ContextProvider } from './context'

// ---------------------------------------------------------------------------
// Public type surface
// ---------------------------------------------------------------------------

const stateBrand: unique symbol = Symbol('schmancy.state')
const namespaceBrand: unique symbol = Symbol('schmancy.state.namespace')
// Internal hooks read by clearStates(): the per-instance reset closure and the
// opt-out flag. Module-private — never part of the public State<> surface.
const clearResetBrand: unique symbol = Symbol('schmancy.state.clearReset')
const keepOnClearBrand: unique symbol = Symbol('schmancy.state.keepOnClear')

/**
 * Open registry — augment via `declare module '@mhmo91/schmancy/state'` to
 * bind a TypeScript type to a namespace string. Augmenting flips that
 * namespace to overload (A) of the factory: zero ceremony at the call
 * site, T comes from the registry.
 */
export interface SchmancyStateRegistry {}

export type RegisteredNamespace = keyof SchmancyStateRegistry & string

/** Feature-prefix convention enforced at compile time. */
export type FeatureNamespace = `${string}/${string}`

export type AssertNovel<NS extends string> = NS extends RegisteredNamespace
	? never & { readonly __error: `Namespace "${NS}" already registered` }
	: NS

export type SyncStorage = Exclude<StorageBackend, 'indexeddb'>
export type AsyncStorage = Extract<StorageBackend, 'indexeddb'>
export type IsAsync<S extends StorageBackend> = S extends AsyncStorage ? true : false

// Detect explicit null/undefined in T's union — works in BOTH strict and
// non-strict tsconfigs. The relaxed `null extends T` form fires for every
// T when strictNullChecks is off, which classifies every shape as
// 'nullable'. Distributing `T extends null/undefined` is a structural
// subtype check that doesn't depend on strict mode.
type _Has<T, U> = T extends U ? true : never
type _IsNullable<T> = [_Has<T, null> | _Has<T, undefined>] extends [never] ? false : true

/**
 * Shape classifier — drives the variant write API per T. Order: structural
 * shapes first (Map / Set / Array), primitives, then nullable-union check,
 * then plain object.
 */
export type Kind<T> = [T] extends [Map<unknown, unknown>]
	? 'map'
	: [T] extends [Set<unknown>]
		? 'set'
		: [T] extends [readonly unknown[]]
			? 'array'
			: [T] extends [string | number | boolean | bigint | symbol]
				? 'primitive'
				: _IsNullable<T> extends true
					? 'nullable'
					: [T] extends [object]
						? 'object'
						: 'unknown'

export interface ObjectAPI<T> {
	set(patch: Partial<T>, merge?: boolean): void
	replace(next: T): void
	update(recipe: (draft: Draft<T>) => void): void
	delete<K extends keyof T>(key: K): void
}

export interface MapAPI<T> {
	set: T extends Map<infer K, infer V> ? (key: K, value: V) => void : never
	replace(next: T): void
	delete: T extends Map<infer K, unknown> ? (key: K) => void : never
	clear(): void
}

export interface SetAPI<T> {
	add: T extends Set<infer U> ? (value: U) => void : never
	delete: T extends Set<infer U> ? (value: U) => boolean : never
	toggle: T extends Set<infer U> ? (value: U) => void : never
	replace(next: T): void
	clear(): void
}

export interface ArrayAPI<T> {
	push: T extends readonly (infer U)[] ? (...items: U[]) => void : never
	replace(next: T): void
	update(recipe: (draft: Draft<T>) => void): void
	clear(): void
}

export interface ScalarAPI<T> {
	set(next: T): void
	replace(next: T): void
}

export type WriteAPI<T> =
	Kind<T> extends 'map'
		? MapAPI<T>
		: Kind<T> extends 'set'
			? SetAPI<T>
			: Kind<T> extends 'array'
				? ArrayAPI<T>
				: Kind<T> extends 'object'
					? ObjectAPI<T>
					: ScalarAPI<T>

export interface BaseAPI<NS extends string, T, S extends StorageBackend> {
	readonly [stateBrand]: true
	readonly namespace: NS & { readonly [namespaceBrand]: NS }
	readonly storage: S
	/**
	 * Current value. For sync backends (memory/local/session) the load is
	 * synchronous, so the type narrows to `T`. For `idb` the load is genuinely
	 * async — `value` is `T | undefined` until `ready` resolves. Read with
	 * `state.value ?? state.defaultValue` if you don't care about the
	 * pre-load distinction; await `state.ready` if you do.
	 */
	readonly value: IsAsync<S> extends true ? T | undefined : T
	readonly defaultValue: T
	readonly ready: Promise<void>
	readonly loaded: boolean
	readonly signal: Signal.State<T>
	readonly $: Observable<T>
	destroy(): void
	/** Internal: build a fresh per-`<schmancy-context>` copy seeded with the
	 *  current value. Exposed on the type so a state can be passed to
	 *  `<schmancy-context provides={[…]}>` without casts. Not for end-user
	 *  code — the leading underscore signals "internal API". */
	isolatedInstance(): { destroy(): void } & Record<string | symbol, unknown>
}

export interface SyncState<NS extends string, T, S extends SyncStorage> extends BaseAPI<NS, T, S>, Disposable {
	[Symbol.dispose](): void
}

export interface AsyncState<NS extends string, T> extends BaseAPI<NS, T, 'indexeddb'>, Disposable, AsyncDisposable {
	[Symbol.dispose](): void
	[Symbol.asyncDispose](): Promise<void>
}

export type State<NS extends string, T, S extends StorageBackend> = S extends AsyncStorage
	? AsyncState<NS, T> & WriteAPI<T>
	: SyncState<NS, T, S extends SyncStorage ? S : never> & WriteAPI<T>

/**
 * Structural safeParse contract — anything shaped like a zod schema
 * satisfies this without schmancy taking a zod dependency: a zod schema,
 * a valibot compat wrapper, or a hand-written validator object all work.
 */
export interface RestoreSchema<T> {
	safeParse(value: unknown): { success: true; data: T } | { success: false; error: unknown }
}

/** Per-state options accepted by every storage backend initializer. */
export interface StateOptions<T = unknown> {
	/** When true, `clearStates()` leaves this state untouched. For device
	 *  preferences (theme, sound) that must survive an identity change. */
	keepOnClear?: boolean
	/**
	 * Refines restore validation from the default — every restore is
	 * validated; there is no unvalidated path — structural repair against
	 * `initial` to an exact parse. Runs only on a genuine restore — a first
	 * run (nothing in storage yet) never consults either form. On success
	 * the signal is set to the schema's PARSED output, not the raw stored
	 * value, so a schema's own field-level `.catch()`/`.default()` salvage
	 * lands in the signal. On failure the signal keeps `initial`, the
	 * rejection is logged (namespace + error), and the rejected bytes are
	 * left on disk untouched — nothing is destroyed eagerly. There is no
	 * separate onInvalid/salvage callback and no flag to disable validation:
	 * mismatch policy is schema composition (a per-field `.catch()` salvages
	 * per-field, a top-level `.catch(initial)` resets wholesale, a strict
	 * schema keeps `initial` by default).
	 */
	schema?: RestoreSchema<T>
}

export interface NamespaceHandlePinned<NS extends string, T> {
	memory(initial: T, options?: StateOptions<T>): State<NS, T, 'memory'>
	local(initial: T, options?: StateOptions<T>): State<NS, T, 'local'>
	session(initial: T, options?: StateOptions<T>): State<NS, T, 'session'>
	idb(initial: T, options?: StateOptions<T>): State<NS, T, 'indexeddb'>
}

export interface NamespaceHandleInferred<NS extends string> {
	memory<U>(initial: U, options?: StateOptions<U>): State<NS, U, 'memory'>
	local<U>(initial: U, options?: StateOptions<U>): State<NS, U, 'local'>
	session<U>(initial: U, options?: StateOptions<U>): State<NS, U, 'session'>
	idb<U>(initial: U, options?: StateOptions<U>): State<NS, U, 'indexeddb'>
}

// ---------------------------------------------------------------------------
// Runtime — variant detection + state instance construction
// ---------------------------------------------------------------------------

type RuntimeKind = 'map' | 'set' | 'array' | 'object' | 'scalar'

function detectKind(value: unknown): RuntimeKind {
	if (value instanceof Map) return 'map'
	if (value instanceof Set) return 'set'
	if (Array.isArray(value)) return 'array'
	if (value === null || value === undefined) return 'scalar'
	if (typeof value !== 'object') return 'scalar'
	return 'object'
}

// Globalize the namespace-claim set so any number of schmancy module copies
// share a single registry. Without this, source-state and dist-state would
// each maintain their own `claimed` set and could both register the same
// namespace, producing two singletons with the same name.
//
// Module Federation is the load-driver here: when web bundles its own copy
// of schmancy/state and so does owl (the remote), both modules execute and
// each tries to register every namespace its consumers declare. The global
// claim Set is what dedupes that. The instance map below is what makes the
// dedup TRANSPARENT — `state('user/state').memory(null)` returns the same
// bound singleton the first caller got, so consumers don't need to know
// who arrived first.
const CLAIMED_KEY = Symbol.for('schmancy.state.claimed')
const INSTANCES_KEY = Symbol.for('schmancy.state.instances')
const claimedSlot = globalThis as { [CLAIMED_KEY]?: Set<string> }
claimedSlot[CLAIMED_KEY] ??= new Set<string>()
const claimed = claimedSlot[CLAIMED_KEY]!
const instancesSlot = globalThis as { [INSTANCES_KEY]?: Map<string, unknown> }
instancesSlot[INSTANCES_KEY] ??= new Map<string, unknown>()
const instances = instancesSlot[INSTANCES_KEY]!

// ---------------------------------------------------------------------------
// Context resolution.
//
// Every read (`value`, `signal`, `$`) and write (`set`, `replace`, `update`,
// `delete`, `push`, `add`, `toggle`, `clear`) on a state instance routes
// through `resolveContextual`. It asks "is this call happening inside a
// `<schmancy-context provides={[...]}>` subtree?" by dispatching a
// `context-request` event from the active host (resolved via the layered
// fallback in `active-host.ts`). If a provider responds, the call routes to
// the per-context isolated instance; otherwise it falls through to the
// module-scoped global. The decision is cached per-host-per-namespace in a
// WeakMap so repeat reads stay O(1).
// ---------------------------------------------------------------------------

// Globalize the per-host resolver cache so that a write through one schmancy
// copy and a read through another resolve to the same target object — the
// cache keys are DOM elements (process-global), but the cache itself must
// also be shared.
const CACHE_KEY = Symbol.for('schmancy.state.hostResolverCache')
const cacheSlot = globalThis as {
	[CACHE_KEY]?: WeakMap<HTMLElement, Map<string, unknown>>
}
cacheSlot[CACHE_KEY] ??= new WeakMap<HTMLElement, Map<string, unknown>>()
const hostResolverCache = cacheSlot[CACHE_KEY]!

function resolveContextual(namespace: string, fallback: unknown): unknown {
	// DEV guard fires HERE — at read-path entry — before any host resolution.
	// A context read inside a computed is CORRECT when a host is on the stack
	// (the per-context memo resolves it); the guard fires only on the residual
	// misuse — a hostless computed read that fell back to the global. See
	// `_devShouldWarnContextualRead` in active-host.ts.
	if (import.meta.env.DEV && _devShouldWarnContextualRead(namespace)) {
		console.error(
			`[schmancy/state] computed() read of namespace "${namespace}" fell back to the global ` +
				`because computed.get() was invoked outside any render/handler frame — no host was ` +
				`on the _activeHost stack (e.g. called from a native-await continuation). ` +
				`"${namespace}" is currently provided by a <schmancy-context>, so the global ` +
				`singleton was returned instead of the correct per-context isolated copy. ` +
				`Ensure computed.get() is called from inside render() or a host event handler ` +
				`so the active host is on the stack when the read occurs.`,
		)
	}

	const host = resolveActiveHost()
	if (host === undefined) return fallback

	const cached = hostResolverCache.get(host)?.get(namespace)
	if (cached !== undefined) return cached

	let resolved: unknown = undefined
	const ctx = createContext<unknown>(stateContextKey(namespace))
	host.dispatchEvent(
		new ContextRequestEvent(ctx, host, value => {
			resolved = value
		}),
	)

	const result = resolved !== undefined ? resolved : fallback
	let perHost = hostResolverCache.get(host)
	if (!perHost) {
		perHost = new Map<string, unknown>()
		hostResolverCache.set(host, perHost)
	}
	perHost.set(namespace, result)
	return result
}

interface InternalState {
	signal: Signal.State<unknown>
	storage: StorageBackend
	adapter: StorageAdapter<unknown>
	pendingWrite: Promise<void> | null
	scheduledWrite: boolean
	disposed: boolean
}

function scheduleWrite(internal: InternalState): void {
	if (internal.scheduledWrite || internal.disposed) return
	internal.scheduledWrite = true
	queueMicrotask(() => {
		internal.scheduledWrite = false
		if (internal.disposed) return
		internal.pendingWrite = internal.adapter.save(internal.signal.get()).catch(err => {
			console.error('[state] save failed:', err)
		})
	})
}

function signalToObservable<T>(signal: Signal.State<T>): Observable<T> {
	return new Observable<T>(subscriber => {
		subscriber.next(signal.get())
		let scheduled = false
		const watcher = new Signal.subtle.Watcher(() => {
			if (scheduled) return
			scheduled = true
			queueMicrotask(() => {
				scheduled = false
				if (subscriber.closed) return
				subscriber.next(signal.get())
				// Re-arm without signal arg: just clears dirty so the next
				// change re-fires the notify callback. Passing the signal arg
				// here calls producerAccessed() each time, which increments
				// nextProducerIndex and appends this watcher to
				// signal.liveConsumerNode — unbounded growth that makes every
				// driveState.set() sweep O(N) consumers and freeze the thread.
				// Every Watcher in this file re-arms this way; computed()'s
				// makeEntry carries the other one, and it carried this defect
				// unfixed until long after this note was written.
				watcher.watch()
			})
		})
		watcher.watch(signal)
		return () => watcher.unwatch(signal)
	})
}

function buildWriteApi(internal: InternalState, kind: RuntimeKind): Record<string, unknown> {
	const commit = (next: unknown): void => {
		if (internal.disposed) return
		internal.signal.set(next)
		scheduleWrite(internal)
	}

	const common = {
		replace: (next: unknown) => commit(next),
	}

	switch (kind) {
		case 'object':
			return {
				...common,
				set(patch: Record<string, unknown>, merge = true) {
					const current = internal.signal.get() as Record<string, unknown>
					commit(merge ? { ...current, ...patch } : patch)
				},
				update(recipe: (draft: unknown) => void) {
					commit(produce(internal.signal.get(), recipe))
				},
				delete(key: string) {
					const current = internal.signal.get() as Record<string, unknown>
					const next = { ...current }
					delete next[key]
					commit(next)
				},
			}

		case 'map':
			return {
				...common,
				set(key: unknown, value: unknown) {
					const current = internal.signal.get() as Map<unknown, unknown>
					const next = new Map(current)
					next.set(key, value)
					commit(next)
				},
				delete(key: unknown) {
					const current = internal.signal.get() as Map<unknown, unknown>
					const next = new Map(current)
					next.delete(key)
					commit(next)
				},
				clear() {
					commit(new Map())
				},
			}

		case 'set':
			return {
				...common,
				add(value: unknown) {
					const current = internal.signal.get() as Set<unknown>
					if (current.has(value)) return
					const next = new Set(current)
					next.add(value)
					commit(next)
				},
				delete(value: unknown): boolean {
					const current = internal.signal.get() as Set<unknown>
					if (!current.has(value)) return false
					const next = new Set(current)
					next.delete(value)
					commit(next)
					return true
				},
				toggle(value: unknown) {
					const current = internal.signal.get() as Set<unknown>
					const next = new Set(current)
					if (next.has(value)) {
						next.delete(value)
					} else {
						next.add(value)
					}
					commit(next)
				},
				clear() {
					commit(new Set())
				},
			}

		case 'array':
			return {
				...common,
				push(...items: unknown[]) {
					const current = internal.signal.get() as unknown[]
					commit([...current, ...items])
				},
				update(recipe: (draft: unknown) => void) {
					commit(produce(internal.signal.get(), recipe))
				},
				clear() {
					commit([])
				},
			}

		case 'scalar':
		default:
			return {
				...common,
				set(next: unknown) {
					commit(next)
				},
			}
	}
}

interface CreateInstanceArgs {
	namespace: string
	initial: unknown
	storage: StorageBackend
}

interface CreateInstanceOptions {
	/** When true, the returned instance is a per-`<schmancy-context>` copy:
	 *  it does NOT claim the namespace in `claimed`, it does NOT route reads
	 *  or writes through `resolveContextual` (so it can serve as the
	 *  resolution target without recursion), and it does NOT expose
	 *  `isolatedInstance` (you only isolate the global). */
	isolated?: boolean
	/** When true, `clearStates()` skips this instance (device preference). */
	keepOnClear?: boolean
	/** Refines restore validation to an exact parse instead of the default
	 *  structural repair against `initial` — see `StateOptions.schema`. */
	schema?: RestoreSchema<unknown>
}

// ---------------------------------------------------------------------------
// Restore validation — always on, no opt-out. Absent `options.schema`, a
// restored value is repaired node-by-node against `initial` — the runtime
// shape authority every call site already supplies. `options.schema`
// replaces this walk with an exact `safeParse` (see `applyLoaded` below).
// ---------------------------------------------------------------------------

type RepairNodeKind = 'nullish' | 'primitive' | 'array' | 'map' | 'set' | 'plainObject' | 'opaque'

function classifyRepairNode(value: unknown): RepairNodeKind {
	if (value === null || value === undefined) return 'nullish'
	if (Array.isArray(value)) return 'array'
	if (value instanceof Map) return 'map'
	if (value instanceof Set) return 'set'
	const valueType = typeof value
	if (valueType === 'string' || valueType === 'number' || valueType === 'boolean' || valueType === 'bigint') {
		return 'primitive'
	}
	if (valueType !== 'object') return 'opaque' // function, symbol — no structural authority
	const proto = Object.getPrototypeOf(value)
	return proto === Object.prototype || proto === null ? 'plainObject' : 'opaque' // Date, class instance, …
}

// Repairs `stored` against `initial` one node at a time, returning a value
// that always matches initial's runtime shape at every node initial has an
// opinion about. `repairedPaths` accumulates a dotted path per repaired node
// so the caller can warn once with the full list.
function repairNode(stored: unknown, initial: unknown, path: string, repairedPaths: string[]): unknown {
	switch (classifyRepairNode(initial)) {
		case 'nullish':
			// No authority at this node — also the escape hatch for a
			// deliberately-loose state like state('x/y').memory(null).
			return stored
		case 'primitive':
			// `typeof` is the whole authority here — a string union accepts any
			// string, so a persisted member set reaches for `schema`.
			if (typeof stored === typeof initial) return stored
			repairedPaths.push(path || '(root)')
			return initial
		case 'array':
			// Element shape is unknowable from `initial` in general — a
			// documented limit; reach for `schema` when exactness matters.
			if (Array.isArray(stored)) return stored
			repairedPaths.push(path || '(root)')
			return initial
		case 'map':
			if (stored instanceof Map) return stored
			repairedPaths.push(path || '(root)')
			return initial
		case 'set':
			if (stored instanceof Set) return stored
			repairedPaths.push(path || '(root)')
			return initial
		case 'plainObject': {
			if (classifyRepairNode(stored) !== 'plainObject') {
				repairedPaths.push(path || '(root)')
				return initial
			}
			const storedRecord = stored as Record<string, unknown>
			const initialRecord = initial as Record<string, unknown>
			// Extra stored keys are retained — a newer build's field, written
			// by another tab, is never destroyed by an older shape's repair.
			const result: Record<string, unknown> = { ...storedRecord }
			for (const key of Object.keys(initialRecord)) {
				result[key] = repairNode(storedRecord[key], initialRecord[key], path ? `${path}.${key}` : key, repairedPaths)
			}
			return result
		}
		case 'opaque':
		default:
			// Function, class instance, Date, symbol — no reliable structural
			// authority to repair against; documented limit.
			return stored
	}
}

function repairAgainstInitial(stored: unknown, initial: unknown, namespace: string): unknown {
	const repairedPaths: string[] = []
	const repaired = repairNode(stored, initial, '', repairedPaths)
	if (repairedPaths.length > 0) {
		console.warn(
			`[state] restore repaired for namespace "${namespace}" — reset to initial at: ${repairedPaths.join(', ')}`,
		)
	}
	return repaired
}

function reportLoadError(err: unknown): void {
	console.error('[state] load failed:', err)
}

function createInstance(args: CreateInstanceArgs, options: CreateInstanceOptions = {}): unknown {
	const { namespace, initial, storage } = args
	const isolated = options.isolated === true
	const adapter = createAdapter<unknown>(storage, namespace)
	const signal = new Signal.State<unknown>(initial)
	const internal: InternalState = {
		signal,
		storage,
		adapter,
		pendingWrite: null,
		scheduledWrite: false,
		disposed: false,
	}

	// Answered-ness is a signal because `loaded` is read from render passes: a
	// closure getter registers no dependency, so `computed(() => cell.loaded)`
	// settles once at `false` and never re-runs, and the consumer keeps a second
	// answered-ness cell of its own — the one nothing re-arms after clearStates().
	const loadedSignal = new Signal.State(false)
	// A clear landing mid-restore wins it: the restore is reading bytes the clear
	// has just disowned — on an identity switch, the PREVIOUS person's — and
	// would otherwise write them back over the wipe, from where the next save
	// persists them to disk again. `disposed` does not cover it; a cleared
	// instance is reset rather than destroyed and serves the next session.
	let clearedBeforeRestore = false
	const applyLoaded = (stored: unknown): void => {
		if (internal.disposed || clearedBeforeRestore) return
		if (stored === null || stored === undefined) return // nothing persisted yet — initial stands, nothing consulted
		if (options.schema) {
			const result = options.schema.safeParse(stored)
			// `'data' in result` rather than `result.success` — schmancy's tsconfig has
			// strictNullChecks off, and a boolean-literal (true/false) discriminant
			// doesn't narrow the union without it; the `in` check does, under both.
			if ('data' in result) {
				internal.signal.set(result.data) // the PARSED value, so a schema's own .catch()/.default() salvage lands here
				return
			}
			// Reject and keep `initial` — the stored bytes are left on disk untouched
			// (no write-through here), so nothing is destroyed until a legitimate write.
			console.warn(
				`[state] restore rejected for namespace "${namespace}" — stored value failed schema validation:`,
				result.error,
			)
			return
		}
		// Default path — always on, no opt-out: structural repair against `initial`.
		internal.signal.set(repairAgainstInitial(stored, initial, namespace))
	}
	const markLoaded = (): void => loadedSignal.set(true)
	const ready = adapter
		.load()
		.then(applyLoaded, reportLoadError)
		.then(markLoaded)
		.then(() => undefined)

	const observable = signalToObservable(signal)
	const writeApi = buildWriteApi(internal, detectKind(initial))

	async function flushAndClose(): Promise<void> {
		// Wait for any in-flight write, then any scheduled microtask write.
		if (internal.pendingWrite) await internal.pendingWrite
		// Drain a possibly-still-scheduled microtask:
		await new Promise<void>(resolve => queueMicrotask(resolve))
		if (internal.pendingWrite) await internal.pendingWrite
		if (adapter.close) await adapter.close()
	}

	const dispose = (): void => {
		if (internal.disposed) return
		internal.disposed = true
		// Fire-and-forget close for sync backends; AsyncDisposable variant
		// has its own awaited path via Symbol.asyncDispose.
		void flushAndClose()
		claimed.delete(namespace)
	}

	const asyncDispose = async (): Promise<void> => {
		if (internal.disposed) return
		internal.disposed = true
		await flushAndClose()
		claimed.delete(namespace)
	}

	// Build the instance with explicit property definitions so the `value`
	// and `loaded` getters stay live. (A `{ ...base }` spread invokes the
	// getter once at spread time and freezes the result — that snapshot
	// then disagrees with the signal on every subsequent write.)
	const instance: Record<string | symbol, unknown> = Object.assign(Object.create(null) as object, {
		[stateBrand]: true,
		namespace,
		storage,
		defaultValue: initial,
		ready,
		destroy: dispose,
		[Symbol.dispose]: dispose,
	})

	Object.defineProperty(instance, 'loaded', {
		get: () => loadedSignal.get(),
		enumerable: true,
	})

	if (storage === 'indexeddb') {
		instance[Symbol.asyncDispose] = asyncDispose
	}

	if (isolated) {
		// Per-context copy. Reads and writes go straight to its own signal —
		// no resolveContextual call, so it serves as a recursion-free
		// resolution target.
		Object.defineProperty(instance, 'value', {
			get: () => signal.get(),
			enumerable: true,
		})
		instance.signal = signal
		instance.$ = observable
		Object.assign(instance, writeApi)
		return instance
	}

	// Global instance. Every public read/write resolves through the active
	// host: inside a `<schmancy-context provides={[…]}>` it routes to the
	// per-context copy; outside, it falls back to this same instance and
	// reads/writes its own signal directly. The cache in `resolveContextual`
	// keeps the lookup O(1) past the first hit.
	const isolatedTarget: Record<string | symbol, unknown> = Object.assign(Object.create(null), {
		signal,
		$: observable,
		...writeApi,
	})
	Object.defineProperty(isolatedTarget, 'value', {
		get: () => signal.get(),
		enumerable: true,
	})

	Object.defineProperty(instance, 'value', {
		get: () => (resolveContextual(namespace, isolatedTarget) as { value: unknown }).value,
		enumerable: true,
	})
	Object.defineProperty(instance, 'signal', {
		get: () => (resolveContextual(namespace, isolatedTarget) as { signal: Signal.State<unknown> }).signal,
		enumerable: true,
	})
	Object.defineProperty(instance, '$', {
		get: () => (resolveContextual(namespace, isolatedTarget) as { $: Observable<unknown> }).$,
		enumerable: true,
	})
	for (const key of Object.keys(writeApi)) {
		instance[key] = (...args: unknown[]): unknown => {
			const target = resolveContextual(namespace, isolatedTarget) as Record<string, (...a: unknown[]) => unknown>
			return target[key](...args)
		}
	}

	// Hook used by `<schmancy-context>` to mint a per-context copy seeded
	// with the current value. Internal: not part of the public State<> type.
	instance.isolatedInstance = (): unknown =>
		createInstance({ namespace, initial: signal.get(), storage: 'memory' }, { isolated: true })

	// clearStates() hooks. The reset operates on THIS global signal/adapter
	// directly (never resolveContextual) so it always wipes the global, and
	// keeps the namespace claimed — reset, not destroy. Completion awaits the
	// write-through flush, mirroring flushAndClose minus adapter.close().
	const clearReset = async (): Promise<void> => {
		if (internal.disposed) return
		if (!loadedSignal.get()) clearedBeforeRestore = true
		let fresh: unknown
		try {
			fresh = structuredClone(initial)
		} catch {
			// Non-cloneable default (functions, class instances) — fall back to the
			// shared reference; the variant write APIs never mutate it in place.
			fresh = initial
		}
		internal.signal.set(fresh)
		scheduleWrite(internal)
		if (internal.pendingWrite) await internal.pendingWrite
		await new Promise<void>(resolve => queueMicrotask(resolve))
		if (internal.pendingWrite) await internal.pendingWrite
	}
	;(instance as Record<symbol, unknown>)[clearResetBrand] = clearReset
	;(instance as Record<symbol, unknown>)[keepOnClearBrand] = options.keepOnClear === true

	return instance
}

function makeHandle(namespace: string): Record<string, (initial: unknown) => unknown> {
	// Idempotent on `(namespace, storage)`. The first caller for a given
	// namespace+storage combination creates the instance; later callers get
	// the same instance back regardless of `initial`. (Initial value is
	// scoped to construction by definition.) This makes the factory safe
	// under module-duplication conditions — Module Federation's loadShare
	// proxies, multiple build-time copies of schmancy, dev/HMR re-evaluation.
	claimed.add(namespace)
	const ensure =
		(storage: 'memory' | 'local' | 'session' | 'indexeddb') => (initial: unknown, options?: StateOptions) => {
			const key = `${namespace}@${storage}`
			const cached = instances.get(key)
			if (cached !== undefined) return cached
			const instance = createInstance(
				{ namespace, initial, storage },
				{ keepOnClear: options?.keepOnClear, schema: options?.schema },
			)
			instances.set(key, instance)
			return instance
		}
	return {
		memory: ensure('memory'),
		local: ensure('local'),
		session: ensure('session'),
		idb: ensure('indexeddb'),
	}
}

// ---------------------------------------------------------------------------
// Three-overload factory.
// (A) registered → pinned T from registry
// (C) unregistered, no type arg → inferred T (typed-const flow)
// (B) unregistered, explicit type arg → pinned T
// Order matters for resolution; do not reorder.
// ---------------------------------------------------------------------------

export function state<NS extends RegisteredNamespace>(
	namespace: NS,
): NamespaceHandlePinned<NS, SchmancyStateRegistry[NS]>

export function state<const NS extends FeatureNamespace>(
	namespace: NS extends RegisteredNamespace ? never : AssertNovel<NS>,
): NamespaceHandleInferred<NS>

export function state<T, const NS extends FeatureNamespace = FeatureNamespace>(
	namespace: NS extends RegisteredNamespace ? never : AssertNovel<NS>,
): NamespaceHandlePinned<NS, T>

export function state(namespace: unknown): unknown {
	if (typeof namespace !== 'string') {
		throw new TypeError('[state] namespace must be a string')
	}
	if (!namespace.includes('/')) {
		throw new TypeError(`[state] namespace "${namespace}" must follow the "feature/name" convention.`)
	}
	return makeHandle(namespace)
}

// ---------------------------------------------------------------------------
// clearStates() — reset every registered global state to its default.
//
// Fired by the app on an identity change (a different person signs in). Each
// registered global state is reset to a fresh copy of its defaultValue and its
// persisted backing is cleared (write-through), EXCEPT states created with
// `{ keepOnClear: true }` (device preferences — theme, sound). Reset, not
// destroy: the namespace stays claimed so the singletons keep working for the
// next person; disposed-but-lingering registry entries are skipped.
//
// Returns an Observable<void> that completes only once every reset has flushed
// to its backing — synchronous in memory, microtask-drained for web storage,
// awaited for IndexedDB. The caller sequences re-hydration after completion so
// the wipe and the next session never overlap.
// ---------------------------------------------------------------------------

export function clearStates(): Observable<void> {
	const resets: Promise<void>[] = []
	for (const inst of instances.values()) {
		const rec = inst as Record<symbol, unknown>
		if (rec[keepOnClearBrand] === true) continue
		const reset = rec[clearResetBrand] as (() => Promise<void>) | undefined
		if (reset) resets.push(reset())
	}
	return from(Promise.all(resets).then(() => undefined))
}

// ---------------------------------------------------------------------------
// Scope-aware derived signal.
//
// Memoizes per the scope its reads resolve within:
//   - ONE shared global memo  when fn reads only module-scoped global state
//     (the common case; today's behavior, zero allocation overhead).
//   - ONE memo per resolved <schmancy-context> isolated instance when fn
//     reads a context-provided state — every host under the same context
//     shares that single derived value.
//
// Scope detection is gated on the memo's own dirty state: a dep change marks
// the scope entry dirty via a per-entry Signal.subtle.Watcher that fires
// synchronously. A clean entry skips _resolveReadScope entirely (no
// ContextRequestEvent, no fn re-run for scope probing), paying only WeakMap
// lookups + watcher re-arm (~330 ns). The full probe runs only on cold or
// dirty reads (dep changed → branch may have flipped → scope must be
// re-confirmed).
//
// Body is plain `fn` (no host/scope closure) so the WeakMap value never
// strong-references its own key, which would leak the entry forever.
//
// Return type is { get(): T } rather than Signal.Computed<T> so the declared
// type is assignable to all 24 existing callers that use only .get(). Internal
// computed instances are plain Signal.Computed<T> registered with SignalWatcher.
// ---------------------------------------------------------------------------

// Sentinel stored in hostScope for "host resolved to global (no provider)".
// Distinct from `undefined` (= "not yet resolved") and `null` (= not used).
const GLOBAL_SCOPE: unique symbol = Symbol('schmancy.computed.globalScope')

type ScopeEntry<T> = {
	computed: Signal.Computed<T>
	watcher: Signal.subtle.Watcher
	dirty: boolean
}

// _resolveReadScope(fn): authoritative scope probe.
//
// Runs fn under Signal.subtle.untrack (no dep registration during the probe)
// with the active host's hostResolverCache entry cleared so resolveContextual
// dispatches a fresh ContextRequestEvent for every namespace fn touches.
// Scans the freshly-populated cache for an entry carrying a `namespace`
// property — that is the resolved isolated instance (present on per-context
// copies but absent on global fallbacks). Returns it or null for global-only.
//
// Single-context only: uses the first isolated instance found. Multi-context
// (fn reading two different context-provided states) requires a composite key
// — not yet supported.
/* oxlint-disable no-underscore-dangle */
function _resolveReadScope(fn: () => unknown): object | null {
	let capturedScope: object | null = null
	Signal.subtle.untrack(() => {
		const host = resolveActiveHost()
		if (host === undefined) {
			try {
				fn()
			} catch {
				/* probe may throw on first run */
			}
			return
		}
		// Clear this host's cache entry so resolveContextual re-dispatches for
		// every namespace fn reads — branch-variance safety: if fn's branch
		// just flipped we must not reuse a cached resolution from the prior branch.
		const savedCache = hostResolverCache.get(host)
		hostResolverCache.delete(host)
		try {
			fn()
		} catch {
			/* probe may throw on first run */
		}
		const probeCache = hostResolverCache.get(host)
		if (probeCache) {
			for (const v of probeCache.values()) {
				if (v !== null && typeof v === 'object' && 'namespace' in (v as object)) {
					capturedScope = v as object
					break
				}
			}
		}
		// Restore the pre-probe cache, merging any newly discovered namespaces in.
		// Probe result dominates: the probe just cleared and re-resolved every
		// namespace fn touched on THIS call — those results are authoritative.
		// Saved entries only fill in namespaces the probe did NOT touch.
		const merged = probeCache ?? new Map<string, unknown>()
		if (savedCache) {
			for (const [k, v] of savedCache) if (!merged.has(k)) merged.set(k, v)
			hostResolverCache.set(host, merged)
		}
	})
	return capturedScope
}

export function computed<T>(fn: () => T, options?: Signal.Options<T>): { get(): T } {
	// per-context (isolated-instance keyed) memo cache. WeakMap so entries
	// auto-collect when their scope (the isolated instance) is GC'd.
	const byScope = new WeakMap<object, ScopeEntry<T>>()
	// global slot for scope === null (no context provider).
	let globalSlot: ScopeEntry<T> | null = null
	// per-host last-resolved scope: populated on every cold/dirty path so the
	// clean path can find the correct scope entry without re-probing.
	const hostScope = new WeakMap<HTMLElement, object | typeof GLOBAL_SCOPE>()

	const makeEntry = (): ScopeEntry<T> => {
		// Body is plain fn — never closes over host or scope (no-leak invariant).
		// _devWrapComputedCallback sets _inComputed in DEV; is identity in prod.
		const c = new Signal.Computed(_devWrapComputedCallback(fn), options)
		const entry: ScopeEntry<T> = { computed: c, watcher: null!, dirty: false }
		// Watcher fires synchronously on any dep change → marks dirty immediately
		// so the next .get() knows to re-confirm scope (a branch may have flipped).
		entry.watcher = new Signal.subtle.Watcher(() => {
			entry.dirty = true
		})
		// The only watch() that names the signal — the edge it adds is permanent
		// (nothing unwatches it). Every re-arm in get() passes no argument: the
		// notify callback fires once and stays latched until watch() clears it,
		// and a re-arm carrying the signal also calls producerAccessed(), which
		// appends a second live-consumer edge on every dirty read. That grows
		// c.liveConsumerNode without bound and makes each notification sweep it,
		// the same defect signalToObservable above carries a note about — this
		// site had it for as long as that note stood, so a Watcher added here
		// later re-arms argument-less on both counts.
		entry.watcher.watch(c)
		return entry
	}

	const entryFor = (scope: object | null): ScopeEntry<T> => {
		if (scope === null) {
			if (globalSlot === null) globalSlot = makeEntry()
			return globalSlot
		}
		let e = byScope.get(scope)
		if (e === undefined) {
			e = makeEntry()
			byScope.set(scope, e)
		}
		return e
	}

	return {
		get(): T {
			const host = _activeHost.get() // STACK-ONLY — not resolveActiveHost()

			// === GLOBAL PATH (no host on _activeHost stack) ===
			// Reads outside any render/handler frame always get the global memo.
			if (host === undefined) {
				if (globalSlot === null) globalSlot = makeEntry()
				if (globalSlot.dirty) {
					globalSlot.dirty = false
					globalSlot.watcher.watch()
				}
				const v = globalSlot.computed.get()
				globalSlot.watcher.watch()
				return v
			}

			// === HOST PATH ===
			const knownScopeRecord = hostScope.get(host)
			if (knownScopeRecord !== undefined) {
				// Scope was resolved on a prior call for this host.
				const knownScope = knownScopeRecord === GLOBAL_SCOPE ? null : (knownScopeRecord as object)
				const entry = entryFor(knownScope)
				if (!entry.dirty) {
					// CLEAN PATH: scope known, no dep change since last read.
					// Zero ContextRequestEvent, zero fn re-run for probing.
					const v = entry.computed.get()
					entry.watcher.watch()
					return v
				}
				// Entry is dirty — a dep changed, branch may have flipped.
				// Fall through to re-probe scope authoritatively.
			}

			// COLD or DIRTY: run authoritative scope probe.
			const scope = _resolveReadScope(fn as () => unknown)
			hostScope.set(host, scope === null ? GLOBAL_SCOPE : scope)
			const entry = entryFor(scope)
			if (entry.dirty) {
				entry.dirty = false
				entry.watcher.watch()
			}
			const v = entry.computed.get()
			entry.watcher.watch()
			return v
		},
	}
}

// ---------------------------------------------------------------------------
// effect(fn) — run a side-effect function whenever any signal it reads
// changes. Returns a Disposable so the effect can be cleaned up.
//
//   const stop = effect(() => {
//     document.title = `${cart.value.items.length} items`
//   })
//   ...later...
//   stop[Symbol.dispose]()
//
// The effect runs once eagerly to register dependencies, then re-runs
// (microtask-coalesced) whenever any read signal changes.
// ---------------------------------------------------------------------------

export interface EffectHandle extends Disposable {
	[Symbol.dispose](): void
}

export function effect(fn: () => void): EffectHandle {
	let scheduled = false
	let disposed = false
	let watcher: Signal.subtle.Watcher | undefined

	const run = (): void => {
		if (disposed) return
		// Wrap fn in a Computed so signal reads are tracked. The computed's
		// value is unused — we only care about the dependency graph it builds.
		const computation = new Signal.Computed(() => {
			fn()
			return undefined
		})
		// Reading the computed registers our Watcher as a sink for every
		// signal the fn touches.
		watcher?.unwatch()
		watcher = new Signal.subtle.Watcher(() => {
			if (scheduled || disposed) return
			scheduled = true
			queueMicrotask(() => {
				scheduled = false
				if (disposed) return
				run()
			})
		})
		watcher.watch(computation)
		computation.get()
	}

	run()

	return {
		[Symbol.dispose](): void {
			if (disposed) return
			disposed = true
			watcher?.unwatch()
			watcher = undefined
		},
	}
}

// ---------------------------------------------------------------------------
// Observable → state bridge.
// ---------------------------------------------------------------------------

export interface FromObservableOptions {
	storage?: StorageBackend
}

/**
 * Lift an Observable into a state(). The state is initialized with
 * `initial`, then updates on every Observable emission. Cleanup on
 * dispose unsubscribes the source.
 */
export function stateFromObservable<T, NS extends FeatureNamespace>(
	observable: Observable<T>,
	namespace: NS,
	initial: T,
	options: FromObservableOptions = {},
): State<NS, T, 'memory'> {
	const storage = options.storage ?? 'memory'
	const handle = makeHandle(namespace)
	const inst = handle[storage](initial) as State<NS, T, 'memory'> & {
		readonly signal: Signal.State<T>
		[Symbol.dispose](): void
	}
	const subscription = observable.subscribe({
		next: value => inst.signal.set(value),
		error: err => console.error(`[state] from(${namespace}) source errored:`, err),
	})
	const originalDispose = inst[Symbol.dispose].bind(inst)
	;(inst as { [Symbol.dispose]: () => void })[Symbol.dispose] = () => {
		subscription.unsubscribe()
		originalDispose()
	}
	return inst
}

// ---------------------------------------------------------------------------
// bindState(host, source) — Lit ReactiveController helper for one-way binding.
//
// Subscribes in hostConnected, unsubscribes in hostDisconnected, calls
// host.requestUpdate() on every emission. Returned object exposes the same
// `value` and `$` surface as the source so render code reads naturally:
//
//   class CartView extends LitElement {
//     cart = bindState(this, cartState)
//     render() { return html`Items: ${this.cart.value.items.length}` }
//   }
//
// Decorator (`@observe`) form is deliberately deferred — schmancy's
// tsconfig still uses experimentalDecorators=true and flipping to
// TC39 native produces ~9k errors across the codebase. The helper
// gives the same lifecycle guarantees with zero tsconfig churn.
// ---------------------------------------------------------------------------

import type { ReactiveController, ReactiveControllerHost } from 'lit'

/** Permissive duck-typed contract — `State<>` and computed() outputs
 *  (`Signal.Computed<T>` wrapped in a thin shim) all satisfy it once
 *  you supply a `value` getter and an Observable `$`. */
export interface ObservableState<T> {
	readonly value: T
	readonly $: Observable<T>
}

export interface BoundState<T> {
	readonly value: T
	readonly $: Observable<T>
}

class BindStateController<T> implements ReactiveController {
	private subscription: import('rxjs').Subscription | undefined
	private latest: T
	constructor(
		private readonly host: ReactiveControllerHost,
		private readonly source: ObservableState<T>,
	) {
		this.latest = source.value
		host.addController(this)
	}
	get value(): T {
		return this.latest
	}
	get $(): Observable<T> {
		return this.source.$
	}
	hostConnected(): void {
		this.subscription = this.source.$.subscribe(v => {
			this.latest = v
			this.host.requestUpdate()
		})
	}
	hostDisconnected(): void {
		this.subscription?.unsubscribe()
		this.subscription = undefined
	}
}

export function bindState<T>(host: ReactiveControllerHost, source: ObservableState<T>): BoundState<T> {
	return new BindStateController(host, source)
}

// ---------------------------------------------------------------------------
// @observe(source) — legacy property decorator for one-way binding.
//
// Usage:
//   class CartView extends LitElement {
//     @observe(cart) cart!: CartState
//     render() { return html`Items: ${this.cart.items.length}` }
//   }
//
// Reads return the latest value emitted by the source (falling back to
// `source.value` before the first emission). Caller writes are dropped
// with a dev-mode warning — the source is the single source of truth.
//
// Per-instance subscription is wired via Lit's static `addInitializer`,
// which runs at instance construction and gives the decorator access to
// the host. A `ReactiveController` handles the lifecycle: subscribe in
// `hostConnected`, unsubscribe in `hostDisconnected`. Multiple `@observe`
// decorators on the same class register independent controllers — no
// init-order bookkeeping, no prototype-walking.
//
// Legacy decorator (matches the rest of schmancy's @property / @state /
// @query family). Works under the existing `experimentalDecorators: true`
// tsconfig — no migration required.
// ---------------------------------------------------------------------------

interface ObserveHost extends ReactiveControllerHost {
	addController(controller: ReactiveController): void
}

interface LitElementCtor {
	addInitializer(initializer: (host: ObserveHost) => void): void
}

export function observe<T>(source: ObservableState<T>) {
	return function (proto: object, propertyKey: string | symbol): void {
		const storageKey = Symbol(`__observe_${String(propertyKey)}`)

		// Per-prototype accessor — reads return the latest cached value,
		// falling back to `source.value` before the first emission lands.
		// Writes from callers are dropped with a dev warning.
		Object.defineProperty(proto, propertyKey, {
			get(this: Record<symbol, T>): T {
				const cached = this[storageKey]
				return cached !== undefined ? cached : source.value
			},
			set(_value: T): void {
				console.warn(
					`@observe: field "${String(propertyKey)}" is read-only — write ignored. ` +
						`Update the source state directly.`,
				)
			},
			configurable: true,
			enumerable: true,
		})

		// Per-instance subscription via Lit's addInitializer hook.
		const ctor = proto.constructor as unknown as LitElementCtor
		if (typeof ctor.addInitializer !== 'function') {
			throw new TypeError(
				`@observe requires a Lit ReactiveElement subclass — ${proto.constructor.name} ` +
					`does not provide static addInitializer.`,
			)
		}

		ctor.addInitializer((host: ObserveHost) => {
			let subscription: import('rxjs').Subscription | undefined
			host.addController({
				hostConnected(): void {
					subscription = source.$.subscribe(value => {
						;(host as unknown as Record<symbol, T>)[storageKey] = value
						host.requestUpdate()
					})
				},
				hostDisconnected(): void {
					subscription?.unsubscribe()
					subscription = undefined
				},
			})
		})
	}
}

/**
 * RxJS operator that restores `host` as the active schmancy context for every
 * emission. Drop this into any pipeline whose subscriber calls state mutators
 * from an async scheduler that bypasses the Promise.then patch (Firestore
 * onSnapshot, WebSockets, native timers, etc.).
 *
 * ```ts
 * snapshotDoc$(ref).pipe(
 *   schmancyContext(this),
 *   takeUntil(this.disconnecting),
 * ).subscribe(snap => { ws.legalEntity.set(parse(snap)) })
 * ```
 */
export function schmancyContext<T>(host: HTMLElement): MonoTypeOperatorFunction<T> {
	return source =>
		new Observable<T>(subscriber =>
			source.subscribe({
				next: v => _activeHost.run(host, () => subscriber.next(v)),
				error: e => subscriber.error(e),
				complete: () => subscriber.complete(),
			}),
		)
}
